import { Component, Input, OnInit, ViewChild, TemplateRef, AfterViewInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TableConfig, TableColumn } from 'src/app/shared/components/table/table.component';
import { ErrorService } from '../../../../core/services/error.service';
import { ProfileService, UserProfileResponse } from '../../../../core/services/profile.service';
import { UserManagementService, UpdateUserPayload } from '../../../../core/services/user-management.service';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.page.component.html',
    styleUrls: ['./user-profile.page.component.css'],
    standalone: false
})
export class UserProfilePageComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() embeddedInModal = false;
    @Input() selectedUserId: number | null = null;
    @Output() profileSaved = new EventEmitter<{ userId: number; label: string }>();
    @Output() profileDeleted = new EventEmitter<{ userId: number }>();
    @ViewChild('emergencyRoleTemplate') emergencyRoleTemplate!: TemplateRef<any>;

    profileForm!: FormGroup;
    isEditMode = true;

    roleTableConfig: TableConfig = {
        columns: [],
        data: [],
        striped: true,
        hoverable: true,
        bordered: false,
        pagination: false,
        headerClass: 'bg-slate-100/30 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider'
    };

    roleColumns: TableColumn[] = [];

    userTypes = [
        { label: 'Front Desk', value: 'frontDesk', selected: false },
        { label: 'Nurse', value: 'nurse', selected: false },
        { label: 'Doctor', value: 'doctor', selected: false },
        { label: 'Biller', value: 'biller', selected: false }
    ];

    emergencyRoles = [
        { label: 'Front Desk', value: 'frontDesk', selected: false },
        { label: 'Nurse', value: 'nurse', selected: false },
        { label: 'Doctor', value: 'doctor', selected: false }
    ];

    roles: Array<{ name: string; emergency: boolean }> = [];
    private currentRoleIds: number[] = [];

    constructor(
        private fb: FormBuilder,
        private profileService: ProfileService,
        private userManagementService: UserManagementService,
        private errorService: ErrorService
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.loadForCurrentContext();
    }

    ngAfterViewInit(): void {
        this.initTableConfig();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.profileForm) return;
        if (changes['selectedUserId'] && this.embeddedInModal) {
            this.loadForCurrentContext();
        }
    }

    initTableConfig(): void {
        this.roleColumns = [
            {
                key: 'name',
                label: 'Role(s)',
                sortable: false,
                width: '70%'
            },
            {
                key: 'emergency', // This key might not exist in API response, handling in loadUserProfile
                label: 'Emergency Role',
                sortable: false,
                align: 'center',
                cellTemplate: this.emergencyRoleTemplate,
                width: '30%'
            }
        ];

        this.roleTableConfig = {
            ...this.roleTableConfig,
            columns: this.roleColumns,
            data: this.roles
        };
    }

    initForm(): void {
        this.profileForm = this.fb.group({
            userName: ['', Validators.required],
            password: ['', Validators.required],
            confirmPassword: ['', Validators.required],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            description: [''],
            idleTime: [''],
            isActive: [false],
            fromTime: [''],
            toTime: [''],
            emergencyPassword: [''],
            emergencyFromDate: [''],
            emergencyToDate: ['']
        });
    }

    private loadProfile(): void {
        // Always clear previous user values first; page must prefill only from /profile response.
        this.profileForm.reset({
            userName: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            description: '',
            idleTime: '',
            isActive: false,
            fromTime: '',
            toTime: '',
            emergencyPassword: '',
            emergencyFromDate: '',
            emergencyToDate: ''
        });
        this.userTypes = [
            { label: 'Front Desk', value: 'frontDesk', selected: false },
            { label: 'Nurse', value: 'nurse', selected: false },
            { label: 'Doctor', value: 'doctor', selected: false },
            { label: 'Biller', value: 'biller', selected: false }
        ];
        this.emergencyRoles = [
            { label: 'Front Desk', value: 'frontDesk', selected: false },
            { label: 'Nurse', value: 'nurse', selected: false },
            { label: 'Doctor', value: 'doctor', selected: false }
        ];
        this.roles = [];
        this.initTableConfig();

        this.profileService.getProfile().subscribe({
            next: (profile: UserProfileResponse) => {
                const normalizeTime = (value: string | null | undefined): string => {
                    if (!value) return '';
                    const isoMatch = String(value).match(/T(\d{2}):(\d{2})/);
                    if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;
                    const hhmmMatch = String(value).match(/^(\d{2}):(\d{2})/);
                    if (hhmmMatch) return `${hhmmMatch[1]}:${hhmmMatch[2]}`;
                    return '';
                };
                this.profileForm.patchValue({
                    userName: profile?.userName ?? '',
                    password: profile?.password ?? '',
                    confirmPassword: profile?.password ?? '',
                    firstName: profile?.userFirstName ?? '',
                    lastName: profile?.userLastName ?? '',
                    description: profile?.userDescription ?? '',
                    idleTime: profile?.userIdleTime != null ? String(profile.userIdleTime) : '',
                    isActive: !!profile?.userIsActive,
                    fromTime: normalizeTime(profile?.userWorkFromTime),
                    toTime: normalizeTime(profile?.userWorkToTime),
                    emergencyPassword: profile?.userEmergencyPassword ?? '',
                    emergencyFromDate: profile?.userStartEmergencyRoleDate ?? '',
                    emergencyToDate: profile?.userEndEmergencyRoleDate ?? ''
                });

                this.userTypes = [
                    { label: 'Front Desk', value: 'frontDesk', selected: !!profile?.userTypeFrontDesk },
                    { label: 'Nurse', value: 'nurse', selected: !!profile?.userTypeNurse },
                    { label: 'Doctor', value: 'doctor', selected: !!profile?.userTypeDoctor },
                    { label: 'Biller', value: 'biller', selected: !!profile?.userTypeBiller }
                ];

                this.emergencyRoles = [
                    { label: 'Front Desk', value: 'frontDesk', selected: !!profile?.userTypeEmergencyFrontDesk },
                    { label: 'Nurse', value: 'nurse', selected: !!profile?.userTypeEmergencyNurse },
                    { label: 'Doctor', value: 'doctor', selected: !!profile?.userTypeEmergencyDoctor }
                ];

                this.roles = (profile?.roles || []).map((r) => ({
                    name: r.roleName,
                    emergency: false
                }));
                this.initTableConfig();
            },
            error: (err) => {
                const status = err?.status;
                if (status === 401) {
                    this.errorService.showError('Unauthorized. Please login again.');
                } else if (status === 404) {
                    this.errorService.showError('User profile not found.');
                } else {
                    this.errorService.showError(err?.error?.message || 'Failed to load profile.');
                }
            }
        });
    }

    private loadForCurrentContext(): void {
        if (this.embeddedInModal && this.selectedUserId != null) {
            this.loadProfileUser(this.selectedUserId);
            return;
        }
        this.loadProfile();
    }

    private loadProfileUser(userId: number): void {
        this.profileForm.reset({
            userName: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            description: '',
            idleTime: '',
            isActive: false,
            fromTime: '',
            toTime: '',
            emergencyPassword: '',
            emergencyFromDate: '',
            emergencyToDate: ''
        });

        this.userManagementService.getProfileUser(userId).subscribe({
            next: (profile) => {
                const normalizeTime = (value: string | null | undefined): string => {
                    if (!value) return '';
                    const isoMatch = String(value).match(/T(\d{2}):(\d{2})/);
                    if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;
                    const hhmmssMatch = String(value).match(/^(\d{2}):(\d{2})(?::\d{2})?/);
                    if (hhmmssMatch) return `${hhmmssMatch[1]}:${hhmmssMatch[2]}`;
                    return '';
                };

                this.profileForm.patchValue({
                    userName: profile?.userName ?? '',
                    password: profile?.password ?? '',
                    confirmPassword: profile?.password ?? '',
                    firstName: profile?.userFirstName ?? '',
                    lastName: profile?.userLastName ?? '',
                    description: profile?.userDescription ?? '',
                    idleTime: profile?.userIdleTime != null ? String(profile.userIdleTime) : '',
                    isActive: !!profile?.userIsActive,
                    fromTime: normalizeTime(profile?.userWorkFromTime),
                    toTime: normalizeTime(profile?.userWorkToTime),
                    emergencyPassword: profile?.userEmergencyPassword ?? '',
                    emergencyFromDate: profile?.userStartEmergencyRoleDate ?? '',
                    emergencyToDate: profile?.userEndEmergencyRoleDate ?? ''
                });

                this.userTypes = [
                    { label: 'Front Desk', value: 'frontDesk', selected: !!profile?.userTypeFrontDesk },
                    { label: 'Nurse', value: 'nurse', selected: !!profile?.userTypeNurse },
                    { label: 'Doctor', value: 'doctor', selected: !!profile?.userTypeDoctor },
                    { label: 'Biller', value: 'biller', selected: !!profile?.userTypeBiller }
                ];

                this.emergencyRoles = [
                    { label: 'Front Desk', value: 'frontDesk', selected: !!profile?.userTypeEmergencyFrontDesk },
                    { label: 'Nurse', value: 'nurse', selected: !!profile?.userTypeEmergencyNurse },
                    { label: 'Doctor', value: 'doctor', selected: !!profile?.userTypeEmergencyDoctor }
                ];

                this.roles = (profile?.roles || []).map((r) => ({
                    name: r.roleName ?? '',
                    emergency: false
                }));
                this.currentRoleIds = Array.isArray(profile?.roles)
                    ? profile.roles.map((r) => Number(r.roleId)).filter((id) => Number.isFinite(id) && id > 0)
                    : [];
                this.initTableConfig();
            },
            error: (err) => {
                this.errorService.showError(err?.error?.message ?? err?.message ?? 'Failed to load user details.');
            }
        });
    }

    saveProfile(): void {
        if (!this.embeddedInModal || this.selectedUserId == null) {
            if (this.profileForm.valid) {
                console.log('Profile saved:', this.profileForm.value);
                this.isEditMode = false;
            }
            return;
        }

        const v = this.profileForm.value ?? {};
        const toTime = (raw: unknown): string | undefined => {
            const value = String(raw ?? '').trim();
            if (!value) return undefined;
            if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
            if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
            return undefined;
        };
        const payload: UpdateUserPayload = {
            userFirstName: String(v.firstName ?? '').trim() || undefined,
            userLastName: String(v.lastName ?? '').trim() || undefined,
            userDescription: String(v.description ?? '').trim() || undefined,
            userTypeFrontDesk: !!this.userTypes.find((t) => t.value === 'frontDesk')?.selected,
            userTypeDoctor: !!this.userTypes.find((t) => t.value === 'doctor')?.selected,
            userTypeNurse: !!this.userTypes.find((t) => t.value === 'nurse')?.selected,
            userTypeBiller: !!this.userTypes.find((t) => t.value === 'biller')?.selected,
            userWorkFromTime: toTime(v.fromTime),
            userWorkToTime: toTime(v.toTime),
            userIdleTime: Number.isFinite(Number(v.idleTime)) ? Number(v.idleTime) : undefined,
            userIsActive: !!v.isActive,
            roleIds: this.currentRoleIds.length ? this.currentRoleIds : undefined
        };

        this.userManagementService.updateProfileUser(this.selectedUserId, payload).subscribe({
            next: (res: any) => {
                const d = res?.data ?? {};
                const firstName = String(d?.userFirstName ?? payload.userFirstName ?? '').trim();
                const lastName = String(d?.userLastName ?? payload.userLastName ?? '').trim();
                const label = `${firstName} ${lastName}`.trim() || String(v.userName ?? '').trim();
                this.errorService.showSuccess(res?.message ?? 'User updated successfully.');
                this.profileSaved.emit({ userId: this.selectedUserId as number, label });
            },
            error: (err) => {
                this.errorService.showError(err?.error?.message ?? err?.message ?? 'Failed to update user.');
            }
        });
    }

    deleteProfile(): void {
        if (!this.embeddedInModal || this.selectedUserId == null) return;
        this.userManagementService.deleteUser(this.selectedUserId).subscribe({
            next: (res) => {
                this.errorService.showSuccess(res?.message ?? 'User deleted successfully.');
                this.profileDeleted.emit({ userId: this.selectedUserId as number });
            },
            error: (err) => {
                this.errorService.showError(err?.error?.message ?? err?.message ?? 'Failed to delete user.');
            }
        });
    }

    toggleEditMode(): void {
        this.isEditMode = !this.isEditMode;
    }
}
